import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { DateTimeField } from "../../components/ui/DateTimeField";
import { FilterBar } from "../../components/ui/FilterBar";
import { Input } from "../../components/ui/Input";
import { JsonViewer } from "../../components/ui/JsonViewer";
import { Modal } from "../../components/ui/Modal";
import { PageHeader } from "../../components/ui/PageHeader";
import { Select } from "../../components/ui/Select";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { type Column, Table } from "../../components/ui/Table";
import { ApiError } from "../../lib/api";
import {
  type AuditFilters,
  type AuditGroupActorRow,
  type AuditGroupTargetRow,
  type AuditLogRow,
  auditApi,
} from "../../lib/api/audit";
import { auditActionLabel, auditActorTypeLabel, auditTargetTypeLabel } from "../../lib/auditLabels";
import { formatDateTime } from "../../lib/format";
import { t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

const ACTOR_TYPES = ["user", "device", "system"];

type ViewMode = "flat" | "byActor" | "byTarget";

interface DrillPin {
  kind: "actor" | "target";
  type: string;
  id: string | null;
  label: string;
}

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

export function AuditLogPage() {
  useLocale();
  const [viewMode, setViewMode] = useState<ViewMode>("flat");
  const [action, setAction] = useState("");
  const [actorType, setActorType] = useState("");
  const [targetType, setTargetType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [focused, setFocused] = useState<AuditLogRow | null>(null);
  const [drillPin, setDrillPin] = useState<DrillPin | null>(null);

  const baseFilters: AuditFilters = {
    action: action || undefined,
    actor_type: actorType || undefined,
    target_type: targetType || undefined,
    date_from: dateFrom ? new Date(dateFrom).toISOString() : undefined,
    date_to: dateTo ? new Date(dateTo).toISOString() : undefined,
    search: search || undefined,
  };

  const flatFilters: AuditFilters =
    drillPin && viewMode === "flat"
      ? {
          ...baseFilters,
          ...(drillPin.kind === "actor"
            ? { actor_type: drillPin.type, actor_id: drillPin.id ?? undefined }
            : { target_type: drillPin.type, target_id: drillPin.id ?? undefined }),
        }
      : baseFilters;

  const list = useQuery({
    enabled: viewMode === "flat",
    queryKey: ["audit", "flat", flatFilters],
    queryFn: () => auditApi.list(flatFilters),
  });

  const actorGroups = useQuery({
    enabled: viewMode === "byActor",
    queryKey: ["audit", "byActor", baseFilters],
    queryFn: () => auditApi.groupByActor(baseFilters),
  });

  const targetGroups = useQuery({
    enabled: viewMode === "byTarget",
    queryKey: ["audit", "byTarget", baseFilters],
    queryFn: () => auditApi.groupByTarget(baseFilters),
  });

  const switchMode = (next: ViewMode) => {
    setViewMode(next);
    setDrillPin(null);
  };

  const drillIntoActor = (row: AuditGroupActorRow) => {
    setDrillPin({
      kind: "actor",
      type: row.actor_type,
      id: row.actor_id,
      label: row.actor_label || auditActorTypeLabel(row.actor_type),
    });
    setViewMode("flat");
  };
  const drillIntoTarget = (row: AuditGroupTargetRow) => {
    setDrillPin({
      kind: "target",
      type: row.target_type,
      id: row.target_id,
      label: row.target_label || auditTargetTypeLabel(row.target_type),
    });
    setViewMode("flat");
  };

  const flatColumns: Column<AuditLogRow>[] = [
    {
      key: "created",
      header: t("audit.column.createdAt"),
      render: (row) => <span className="mono">{formatDateTime(row.created_at)}</span>,
    },
    {
      key: "action",
      header: t("audit.column.action"),
      render: (row) => (
        <span className="badge badge--info" title={row.action}>
          {auditActionLabel(row.action)}
        </span>
      ),
    },
    {
      key: "actor",
      header: t("audit.column.actor"),
      render: (row) => <Badge tone="neutral">{auditActorTypeLabel(row.actor_type)}</Badge>,
    },
    {
      key: "target",
      header: t("audit.column.target"),
      render: (row) => (
        <span style={{ fontSize: 12 }}>
          {auditTargetTypeLabel(row.target_type)}
          {row.target_id ? (
            <span className="mono" style={{ marginLeft: 6 }}>
              · {row.target_id.slice(0, 8)}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: "ip",
      header: t("audit.column.ip"),
      render: (row) => <span className="mono">{row.ip || "—"}</span>,
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <Button variant="secondary" size="sm" onClick={() => setFocused(row)}>
          {t("audit.column.viewPayload")}
        </Button>
      ),
      align: "right",
    },
  ];

  const actorColumns: Column<AuditGroupActorRow>[] = [
    {
      key: "actor",
      header: t("audit.groupBy.columns.identity"),
      render: (row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span>{row.actor_label || t("audit.groupBy.unknownActor")}</span>
          <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
            {auditActorTypeLabel(row.actor_type)}
            {row.actor_id ? <span className="mono"> · {row.actor_id.slice(0, 8)}</span> : null}
          </span>
        </div>
      ),
    },
    {
      key: "count",
      header: t("audit.groupBy.columns.count"),
      render: (row) => row.count.toLocaleString(),
      align: "right",
    },
    {
      key: "lastAt",
      header: t("audit.groupBy.columns.lastSeen"),
      render: (row) => <span className="mono">{formatDateTime(row.last_at)}</span>,
    },
    {
      key: "drill",
      header: "",
      render: (row) => (
        <Button variant="secondary" size="sm" onClick={() => drillIntoActor(row)}>
          {t("audit.groupBy.columns.expand")}
        </Button>
      ),
      align: "right",
    },
  ];

  const targetColumns: Column<AuditGroupTargetRow>[] = [
    {
      key: "target",
      header: t("audit.groupBy.columns.identity"),
      render: (row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span>{row.target_label || t("audit.groupBy.unknownTarget")}</span>
          <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
            {auditTargetTypeLabel(row.target_type)}
            {row.target_id ? <span className="mono"> · {row.target_id.slice(0, 8)}</span> : null}
          </span>
        </div>
      ),
    },
    {
      key: "count",
      header: t("audit.groupBy.columns.count"),
      render: (row) => row.count.toLocaleString(),
      align: "right",
    },
    {
      key: "lastAt",
      header: t("audit.groupBy.columns.lastSeen"),
      render: (row) => <span className="mono">{formatDateTime(row.last_at)}</span>,
    },
    {
      key: "drill",
      header: "",
      render: (row) => (
        <Button variant="secondary" size="sm" onClick={() => drillIntoTarget(row)}>
          {t("audit.groupBy.columns.expand")}
        </Button>
      ),
      align: "right",
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={t("audit.eyebrow")}
        title={t("audit.title")}
        subtitle={t("audit.subtitle")}
      />

      <div className="stack">
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
            {t("audit.groupBy.label")}
          </span>
          <SegmentedToggle viewMode={viewMode} onChange={switchMode} />
          {drillPin && viewMode === "flat" && (
            <Badge tone="info">
              {drillPin.label}
              <button
                type="button"
                onClick={() => setDrillPin(null)}
                style={{
                  marginLeft: 8,
                  border: 0,
                  background: "transparent",
                  cursor: "pointer",
                  color: "inherit",
                }}
                aria-label={t("common.clear")}
              >
                ×
              </button>
            </Badge>
          )}
        </div>

        <FilterBar>
          <Input
            type="search"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder={t("audit.filter.actionPlaceholder")}
            style={{ width: 220 }}
          />
          <span className="filter-bar__label">{t("audit.filter.actorType")}</span>
          <Select value={actorType} onChange={(e) => setActorType(e.target.value)}>
            <option value="">{t("common.all")}</option>
            {ACTOR_TYPES.map((at) => (
              <option key={at} value={at}>
                {auditActorTypeLabel(at)}
              </option>
            ))}
          </Select>
          <span className="filter-bar__label">{t("audit.filter.targetType")}</span>
          <Input
            type="search"
            value={targetType}
            onChange={(e) => setTargetType(e.target.value)}
            placeholder={t("audit.filter.targetPlaceholder")}
            style={{ width: 200 }}
          />
          <span className="filter-bar__label">{t("audit.filter.dateFrom")}</span>
          <DateTimeField
            value={dateFrom}
            onChange={setDateFrom}
            ariaLabel={t("audit.filter.dateFrom")}
          />
          <span className="filter-bar__label">{t("audit.filter.dateTo")}</span>
          <DateTimeField
            value={dateTo}
            onChange={setDateTo}
            ariaLabel={t("audit.filter.dateTo")}
          />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("common.searchPlaceholder")}
            style={{ width: 200 }}
          />
        </FilterBar>

        <Card padding="none">
          {viewMode === "flat" && (
            <FlatPane
              query={list}
              columns={flatColumns}
              translateError={translateError}
            />
          )}
          {viewMode === "byActor" && (
            <GroupPane
              query={actorGroups}
              columns={actorColumns}
              translateError={translateError}
              rowKey={(row) => `${row.actor_type}:${row.actor_id ?? ""}`}
            />
          )}
          {viewMode === "byTarget" && (
            <GroupPane
              query={targetGroups}
              columns={targetColumns}
              translateError={translateError}
              rowKey={(row) => `${row.target_type}:${row.target_id ?? ""}`}
            />
          )}
        </Card>
      </div>

      <Modal
        open={focused !== null}
        onClose={() => setFocused(null)}
        title={t("audit.payloadModalTitle")}
      >
        {focused && (
          <div className="stack">
            <p style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
              {auditActionLabel(focused.action)} · {formatDateTime(focused.created_at)}
            </p>
            <JsonViewer value={focused.payload_summary} defaultCollapsed={false} />
          </div>
        )}
      </Modal>
    </div>
  );
}

interface SegmentedToggleProps {
  viewMode: ViewMode;
  onChange: (next: ViewMode) => void;
}

function SegmentedToggle({ viewMode, onChange }: SegmentedToggleProps) {
  const items: ReadonlyArray<{ value: ViewMode; labelKey: string }> = [
    { value: "flat", labelKey: "audit.groupBy.flat" },
    { value: "byActor", labelKey: "audit.groupBy.byActor" },
    { value: "byTarget", labelKey: "audit.groupBy.byTarget" },
  ];
  return (
    <div role="tablist" style={{ display: "inline-flex", border: "1px solid var(--color-border-strong)", borderRadius: 6, overflow: "hidden" }}>
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          role="tab"
          aria-selected={viewMode === item.value}
          onClick={() => onChange(item.value)}
          style={{
            padding: "6px 12px",
            border: 0,
            background:
              viewMode === item.value ? "var(--color-accent-soft)" : "var(--color-surface)",
            color: "var(--color-text)",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          {t(item.labelKey)}
        </button>
      ))}
    </div>
  );
}

interface FlatPaneProps {
  query: ReturnType<typeof useQuery<AuditLogRow[]>>;
  columns: Column<AuditLogRow>[];
  translateError: (err: unknown) => string;
}

function FlatPane({ query, columns, translateError }: FlatPaneProps) {
  if (query.isLoading) return <LoadingState />;
  if (query.error) {
    return (
      <ErrorState
        titleKey="states.errorTitle"
        message={translateError(query.error)}
        action={
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            {t("common.retry")}
          </Button>
        }
      />
    );
  }
  if (!query.data) return null;
  return (
    <Table
      rows={query.data}
      columns={columns}
      rowKey={(row) => String(row.id)}
      empty={<EmptyState titleKey="audit.emptyTitle" bodyKey="audit.emptyBody" />}
    />
  );
}

interface GroupPaneProps<T> {
  query: ReturnType<typeof useQuery<T[]>>;
  columns: Column<T>[];
  translateError: (err: unknown) => string;
  rowKey: (row: T) => string;
}

function GroupPane<T>({ query, columns, translateError, rowKey }: GroupPaneProps<T>) {
  if (query.isLoading) return <LoadingState />;
  if (query.error) {
    return (
      <ErrorState
        titleKey="states.errorTitle"
        message={translateError(query.error)}
        action={
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            {t("common.retry")}
          </Button>
        }
      />
    );
  }
  if (!query.data) return null;
  return (
    <Table
      rows={query.data}
      columns={columns}
      rowKey={rowKey}
      empty={<EmptyState titleKey="audit.emptyTitle" bodyKey="audit.emptyBody" />}
    />
  );
}
